import React, { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toPlainText } from "../../design/domain";
import { useApp } from "../../design/AppContext";
import { isReportingRole } from "../../hook/useDailyTaskReminder";
import { currentAccount } from "../../utils/api";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Banner, DataRow, RowButton, StatusChip } from "../../components/mui";
import { InlineLoading } from "../../components/loading";
import { MdCheck, MdChevronLeft, MdChevronRight, MdErrorOutline, MdWarningAmber } from "react-icons/md";

/**
 * 這一週每天記了什麼（路由仍是 /Timesheet）
 *
 * 改版前這一頁是「週工時表」：一列一個工項、一～五各一格數字、估時與實際時數、
 * 右上角一顆「送審」。使用者要的不是那些數字 ——
 *   「RD 它可以看到每天填的工項日誌 不用看時數 時數沒屁用
 *     主要是要看他有沒有記得填 沒有填的日期要紅字提示」
 * 所以整頁改成「一天一張卡」，攤開那一天他記了哪幾筆、每一筆寫了什麼；
 * 沒填的上班日整張卡掛紅色左緣＋「沒有記錄」徽章，最上方再用一句話把日期列出來。
 *
 * 這一頁的重點是**空的那一天**。所以：
 *   · 沒有資料不等於可以整頁空白 —— 後端還沒回來時照樣把整週的日子畫出來，
 *     只是狀態寫「還讀不到」，不會把讀不到說成「沒有填」（那是在冤枉人）。
 *   · 六日與國定假日標成「不用填」，不算漏填、不上紅色。
 *   · **還沒到的上班日也不是漏填**：今天週三，週四週五還沒發生，
 *     標成紅字「沒有記錄」等於憑空指控。以後端的 is_future / is_missing 為準。
 *
 * 「有沒有填」的判定不在前端自己算，一律讀後端給的 filled / is_missing ——
 * 後端那套（MyTaskService.GetTodayStatus／PerformanceService 的漏填偵測）是
 * 「當天建立工項」或「當天更新進度且不是事後補登」，還要扣掉假日表上的日子。
 * 前端照著猜一份就會變成第四套口徑，兩邊各說各話。
 * 只有在後端整個欄位都沒給時才退而求其次由紀錄推（見 normalizeDay），
 * 而那一條也刻意跟後端同一個規則：事後補登的不算當天有記。
 *
 * 後端（GET /Timesheet/WeekLog）每一天回的形狀，欄位名以這一份為準：
 *   date / label / weekday / is_workday / off_reason / holiday_name /
 *   is_today / is_future / filled / is_missing /
 *   progress[]（當天的進度回報：id, task_id, code, title, project, content,
 *               progress, is_backfill, is_deleted, time）/
 *   created_tasks[]（當天建立的工項：task_id, code, title, project, is_deleted）/
 *   hidden_count（那天有紀錄、但工項不在你看得到的專案裡的筆數）
 * 兩個陣列而不是一個 entries：「開了一張單」與「推了進度」在畫面上要講得不一樣，
 * 而每一筆裡面並沒有 kind 欄位可以事後分辨（kind 由 normalizeEntry 的呼叫端給）。
 *
 * 路由沒有換：/Timesheet 保留，既有的連結（MyTask 的「看這一週」、
 * Dashboard 的捷徑、側邊欄）不會壞。側邊欄的標籤在 resources/menu.js 改掉了。
 *
 * 版面規則（src/styles/README.md）：
 *   · 外層不再包頁面容器（Layout.js 的內容區已經處理），也不自己寫 max-width。
 *   · 顏色走 MUI 主題、字級一律 --t-*（Typography variant）。
 *   · 整頁最多一顆主要按鈕（contained）—— 留給「今天還沒記」時的那顆去回報。
 */

/* ===================== 小工具 ===================== */

const WEEKDAY = ["日", "一", "二", "三", "四", "五", "六"];

/** 後端欄位可能是 snake_case / camelCase / PascalCase，缺了就給 fallback，不讓整頁掛掉 */
function pick(obj, keys, fallback) {
  if (!obj) return fallback;
  for (let i = 0; i < keys.length; i += 1) {
    const v = obj[keys[i]];
    if (v !== undefined && v !== null) return v;
  }
  return fallback;
}

/**
 * 後端的日期字串 → 本地 Date（只取到「哪一天」）。
 *
 * 只截前十碼再用本地時間建構，不直接 new Date(整串)：
 * 同一個「哪一天」在資料庫裡有兩種 offset（畫面建立的存成前一天 16:00Z、
 * SQL 灌的是 00:00），交給瀏覽器照自己的時區換算，台北的 9/22 會變成 9/21，
 * 整欄日期就整批往前挪一天。後端已經一律換成台北日期才回，前端照字面讀即可。
 */
function toDate(v) {
  if (!v) return null;
  const s = String(v);
  const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s.replace(/-/g, "/"));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** 以本地時區組 YYYY-MM-DD，避免 toISOString 因 UTC 位移跨日 */
function ymd(d) {
  const mo = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${mo}-${day}`;
}

/** 顯示用的 M/D */
const md = (d) => `${d.getMonth() + 1}/${d.getDate()}`;

/** 取某日所在週的週一（每週以週一起算，與後端 WeekStartOf 同一個起點） */
function mondayOf(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // 週日(0) 視為第 6 天
  return d;
}

/**
 * 那一筆紀錄是幾點寫的。只有日期沒有時間就不寫，不要生一個 00:00 出來。
 *
 * 後端的 time 已經是換算好的台北 "14:32"（見 BusinessDate.ToLocal），
 * 所以開頭也要認 —— 只比對 ISO 字串裡的 "T14:32" 的話，"14:32" 會整個比不中，
 * 鍵名接對了時間欄位照樣是空的。
 */
function hhmm(v) {
  if (!v) return "";
  const s = String(v).trim();
  const m = s.match(/(?:^|[T\s])(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, "0")}:${m[2]}` : "";
}

/* ===================== 後端回傳 → 畫面用的形狀 ===================== */

/**
 * 一筆紀錄（當天的進度回報，或當天建立的工項）。
 *
 * kind 由呼叫端指定，不從紀錄本身猜：後端 /Timesheet/WeekLog 是用**兩個陣列**
 * （progress 與 created_tasks）區分這兩件事的，每一筆裡沒有 kind 這個欄位。
 * 在這裡讀 e.kind 的話兩種都會落到預設值，當天新開的單會被標成「更新進度」——
 * 主管以為他推進了什麼，其實他只是開了一張單。
 *
 * 欄位名照後端實際回的鍵（code / project / time），camelCase 與 PascalCase
 * 只是留給序列化設定改動時的保險，不是這支端點會回的形狀。
 *
 * content 是編輯器打出來的 HTML，用既有的 toPlainText 轉純文字 ——
 * 這一頁一筆只給一行，直接 innerHTML 既有 XSS 也會把圖片跟排版一起塞進列裡。
 */
function normalizeEntry(raw, i, kind) {
  const e = raw || {};
  const taskId = pick(e, ["task_id", "taskId", "TaskId", "id", "Id"], null);
  const progressRaw = Number(pick(e, ["progress", "Progress"], NaN));

  return {
    key: `${kind}-${taskId != null ? taskId : "x"}-${i}`,
    /* 只分「建立工項」與「更新進度」兩種 */
    kind: kind === "task" ? "task" : "progress",
    taskId: taskId === "" ? null : taskId,
    issueKey: String(pick(e, ["code", "Code", "issue_key", "issueKey", "IssueKey"], "") || ""),
    title: String(pick(e, ["title", "Title", "name", "Name"], "") || ""),
    projectName: String(pick(e, ["project", "Project", "project_name", "projectName", "ProjectName"], "") || ""),
    content: toPlainText(pick(e, ["content", "Content", "body", "Body"], "")),
    /* 進度只有回報才有；當天建立的工項沒有這個欄位，不要印一個「進度 0%」出來 */
    progress: kind === "task" || !Number.isFinite(progressRaw) ? null : progressRaw,
    backfill: pick(e, ["is_backfill", "isBackfill", "IsBackfill", "backfill"], false) === true,
    at: hhmm(pick(e, ["time", "Time", "at", "At", "create_at", "createAt", "CreateAt"], "")),
  };
}

/**
 * 一天。
 *
 * 紀錄來自後端的**兩個陣列**：progress（當天的進度回報）與 created_tasks
 * （當天建立的工項）。這裡合成一份列表，但保留 kind —— 兩件事在畫面上要講得不一樣。
 * entries／items／logs 只是舊形狀的保險，這支端點不會回那些鍵。
 *
 * state 是這一頁唯一要講的事，五種：
 *   filled  有記錄
 *   missing 上班日、已經到了、沒記錄 ← 這一頁的主角
 *   future  上班日、但那一天還沒到 —— 不是漏填，更不能標紅
 *   rest    六日／國定假日，不用填
 *   unknown 後端還沒給答案（欄位缺、或這一週整包沒回來）
 *
 * 「已經到了沒有」以後端的 is_future / is_missing 為準：後端拿的是台北營運日
 * （BusinessDate.Today），瀏覽器拿的是使用者自己的時區，兩邊會在跨日前後差一天。
 * 後端沒給時才退回自己比 todayKey。
 */
function normalizeDay(raw, i, todayKey) {
  const d = raw || {};
  const date = toDate(pick(d, ["date", "Date", "work_date", "workDate", "ymd"], ""));

  const rawProgress = pick(d, ["progress", "Progress", "entries", "Entries", "items", "Items", "logs"], []);
  const rawCreated = pick(d, ["created_tasks", "createdTasks", "CreatedTasks"], []);
  /* 當天開的單排前面，進度回報跟在後面（後端已經按時間排好） */
  const entries = [
    ...(Array.isArray(rawCreated) ? rawCreated : []).map((e, n) => normalizeEntry(e, n, "task")),
    ...(Array.isArray(rawProgress) ? rawProgress : []).map((e, n) => normalizeEntry(e, n, "progress")),
  ];

  /* 那天確實有紀錄、但工項不在我看得到的專案裡，後端只給數量不給內容。
     不加這一段的話，畫面會把「你看不到」說成「他沒填」 */
  const hiddenRaw = Number(pick(d, ["hidden_count", "hiddenCount", "HiddenCount"], 0));
  const hiddenCount = Number.isFinite(hiddenRaw) && hiddenRaw > 0 ? hiddenRaw : 0;

  /* is_workday 缺了就退回「六日不是上班日」—— 國定假日前端沒有假日表，猜不到，
     所以只敢用星期幾這件日曆上的事實，其餘交給後端 */
  const workdayRaw = pick(d, ["is_workday", "isWorkday", "IsWorkday", "workday"], null);
  const weekday = date ? date.getDay() : -1;
  const isWorkday = workdayRaw === null ? !(weekday === 0 || weekday === 6) : workdayRaw === true;

  const futureRaw = pick(d, ["is_future", "isFuture", "IsFuture"], null);
  const isFuture =
    futureRaw === null ? !!(date && todayKey && ymd(date) > todayKey) : futureRaw === true;

  /* filled 缺了才自己推，而且用跟後端一樣的規則：事後補登的不算當天有記 */
  const filledRaw = pick(d, ["filled", "Filled", "is_filled", "isFilled", "has_task", "hasTask"], null);
  const known = filledRaw !== null || entries.length > 0;
  const filled = filledRaw === null ? entries.some((e) => !e.backfill) : filledRaw === true;

  /* 後端已經把「這一天算不算漏填」算成一個欄位，missing_days 也是照它組的。
     這裡直接採用，卡片上的紅字與最上面那句話才不會各算各的 */
  const missingRaw = pick(d, ["is_missing", "isMissing", "IsMissing"], null);

  let state = "unknown";
  if (!isWorkday) state = "rest";
  else if (missingRaw === true) state = "missing";
  else if (isFuture) state = "future";
  else if (known) state = filled ? "filled" : "missing";

  /* 休假的理由：國定假日要寫出名稱（「不用填 · 國慶日」），
     週末不寫 —— 日期旁邊已經有「（六）」，再寫一次「週末」只是重複。
     後端的 holiday_name 只在國定假日有值，off_reason 則連週末都有 */
  const holidayName = String(pick(d, ["holiday_name", "holidayName", "HolidayName"], "") || "");
  const offReason = String(pick(d, ["off_reason", "offReason", "OffReason"], "") || "");

  return {
    key: date ? ymd(date) : `d${i}`,
    date,
    label: date ? `${md(date)}（${WEEKDAY[date.getDay()]}）` : "—",
    isWorkday,
    /* 舊版後端只有 off_reason 時退而求其次；"週末" 那一種不當成假日名稱 */
    holidayName: holidayName || (offReason && offReason !== "週末" ? offReason : ""),
    state,
    entries,
    hiddenCount,
  };
}

/** 後端沒回（或還沒回）這一週時，照日曆自己把七天排出來：空的那一天才是這一頁的重點 */
function skeletonDays(weekStart, todayKey) {
  return Array.from({ length: 7 }, (_v, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return normalizeDay({ date: ymd(d) }, i, todayKey);
  });
}

/* ===================== 頁面 ===================== */

function App(props) {
  const { week, GET_TimesheetWeek } = props;
  const { userInfo } = useApp();
  const navigate = useNavigate();
  const [search] = useSearchParams();

  const me = currentAccount();
  /* ?account= 是主管看別人的入口；沒帶就是看自己。後端 /Timesheet/Week 本來就收這個參數 */
  const target = (search.get("account") || "").trim() || me;
  const isOther = !!target && !!me && target !== me;

  const [weekOffset, setWeekOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  /** redux 裡的那包是哪一週的：取回成功才更新，免得上一週的資料掛在新的區間標籤下 */
  const [loadedOffset, setLoadedOffset] = useState(null);

  const fetchWeek = useCallback(
    (offset) => {
      GET_TimesheetWeek({ weekOffset: offset, account: target }, () => setLoadedOffset(offset), setLoading);
    },
    [GET_TimesheetWeek, target]
  );

  useEffect(() => {
    setLoadedOffset(null);
    fetchWeek(weekOffset);
  }, [fetchWeek, weekOffset]);

  const ready = loadedOffset === weekOffset;

  /* --- 這一週是哪一段 --- */
  const localStart = useMemo(() => {
    const d = mondayOf(new Date());
    d.setDate(d.getDate() + weekOffset * 7);
    return d;
  }, [weekOffset]);

  /*
   * 「今天是哪一天」以後端為準（台北營運日，見 BusinessDate.Today）。
   * 人在別的時區時，瀏覽器的今天會跟營運日差一天，是否「還沒到」就會兩邊各說各話。
   * 後端沒給才退回瀏覽器的今天。
   */
  const todayKey = useMemo(() => {
    const fromApi = String(pick(week, ["today", "Today"], "") || "").slice(0, 10);
    return ready && /^\d{4}-\d{2}-\d{2}$/.test(fromApi) ? fromApi : ymd(new Date());
  }, [week, ready]);

  const days = useMemo(() => {
    if (!ready) return skeletonDays(localStart, todayKey);
    const raw = pick(week, ["days", "Days"], []);
    /*
     * 舊的 /Timesheet/Week 也有一個 days，但那是 ["一","二","三","四","五"] 的星期字串，
     * 不是每日日誌。只收得下物件的那一種，字串那一種當成「後端還沒換過來」，
     * 讓畫面退回骨架＋「還讀不到」，不要拿星期名稱硬畫成七張空卡再說人家沒填。
     */
    const list = (Array.isArray(raw) ? raw : []).filter((x) => x && typeof x === "object");
    if (!list.length) return skeletonDays(localStart, todayKey);
    return list
      .map((d, i) => normalizeDay(d, i, todayKey))
      .sort((a, b) => (a.date && b.date ? a.date - b.date : 0));
  }, [week, ready, localStart, todayKey]);

  const weekStart = useMemo(() => {
    const fromApi = toDate(pick(week, ["week_start", "weekStart", "WeekStart"], ""));
    if (ready && fromApi) return fromApi;
    const first = days.find((d) => d.date);
    return ready && first ? first.date : localStart;
  }, [week, ready, days, localStart]);

  const weekEnd = useMemo(() => {
    const fromApi = toDate(pick(week, ["week_end", "weekEnd", "WeekEnd"], ""));
    if (ready && fromApi) return fromApi;
    const last = [...days].reverse().find((d) => d.date);
    if (ready && last) return last.date;
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 6);
    return d;
  }, [week, ready, days, weekStart]);

  const rangeLabel = `${md(weekStart)} – ${md(weekEnd)}`;

  /* --- 看的是誰 --- */
  const myName =
    pick(userInfo, ["name", "Name", "account", "Account"], "") || me || "您";
  const whoName = pick(week, ["name", "Name"], "") || (isOther ? target : myName);
  const whoLabel = isOther && whoName !== target ? `${whoName}（${target}）` : String(whoName || target || "");

  /* --- 這一週漏了幾天 --- */
  const missingDays = useMemo(() => days.filter((d) => d.state === "missing"), [days]);
  /*
   * 講「都有記錄」只能拿**已經到期**的上班日來講。
   * 這一週一共五個上班日，但今天週三時週四週五還沒發生，
   * 說成「這一週的 5 個上班日都有記錄」是在替兩天還沒過的日子背書。
   */
  const dueWorkdayCount = useMemo(
    () => days.filter((d) => d.isWorkday && d.state !== "future").length,
    [days]
  );
  const futureWorkdayCount = useMemo(
    () => days.filter((d) => d.isWorkday && d.state === "future").length,
    [days]
  );
  const unknown = useMemo(() => days.some((d) => d.state === "unknown"), [days]);

  const todayMissing = missingDays.some((d) => d.date && ymd(d.date) === todayKey);

  /*
   * 「去回報今天做的事」只在「看自己的、今天就是漏的那一天」時才給，而且避開
   * 已經會在每一頁頂端看到全站橫幅的角色（layouts/parts/DailyTaskBanner.js）——
   * 同一句話說兩次只是佔掉兩倍版面。
   * 過去那幾天不給按鈕：後端的回報一律記在送出的當天（POST /MyTask/Progress
   * 沒有日期參數），畫一顆「補 9/22」按下去只會寫成今天，比沒有更糟。
   */
  const showReportBtn =
    !isOther && todayMissing && !isReportingRole(pick(userInfo, ["role", "Role"], ""));

  const openTask = (e) => {
    if (e.taskId === null || e.taskId === undefined || e.taskId === "") return;
    navigate(`/Task/${e.taskId}`);
  };

  /** 一筆紀錄。主管看到一句看不懂的回報時要點得進那件工項 */
  const entryRow = (e) => {
    const canOpen = e.taskId !== null && e.taskId !== undefined && e.taskId !== "";
    const title = [e.issueKey, e.title].filter(Boolean).join(" ") || "（此筆紀錄無工項標題）";
    const metas = [
      e.kind === "task" ? "建立工項" : "更新進度",
      e.projectName,
      e.progress !== null ? `進度 ${e.progress}%` : "",
      e.at,
    ].filter(Boolean);

    const inner = (
      <Box sx={{ flex: "1 1 auto", minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography variant="subtitle1">{title}</Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          component="div"
          sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 2, rowGap: 0.5 }}
        >
          {metas.map((m, i) => (
            <span key={`m${i}`}>{m}</span>
          ))}
          {/* 補登的要標出來：後端的漏填判定不把補登算成「當天有記」，
              畫面上不講的話，這一天明明列著東西卻標成「沒有記錄」會看起來像壞掉 */}
          {e.backfill && (
            <StatusChip kind="warn" size="small">
              事後補登
            </StatusChip>
          )}
        </Typography>
        {/* 回報內容要能換行；沒有空白的長英數（分支名、網址）也要斷得掉，否則整列會被撐寬 */}
        {e.content && <Typography sx={{ overflowWrap: "anywhere" }}>{e.content}</Typography>}
      </Box>
    );

    /* 後端沒給 task_id 的紀錄點不進工項，就不要裝成可點的列（手指形游標與 hover 都是承諾） */
    return canOpen ? (
      <RowButton key={e.key} onClick={() => openTask(e)}>
        {inner}
      </RowButton>
    ) : (
      <DataRow key={e.key}>{inner}</DataRow>
    );
  };

  /*
   * 卡片的狀態外觀：
   *   missing 左緣紅色色條＋日期與說明改紅
   *   rest    頁首灰底、日期退成次要色（不上紅色，也不要跟有記錄的日子一樣重）
   *   future  只把日期退成次要色 —— 那一天還沒發生，沒有什麼好指控的，絕不上紅色
   */
  const dayCardSx = (d) =>
    d.state === "missing" ? { boxShadow: (t) => `inset 4px 0 0 ${t.palette.error.main}` } : {};
  const dayDateColor = (d) => {
    if (d.state === "missing") return "error.dark";
    if (d.state === "rest" || d.state === "future") return "text.secondary";
    return "text.primary";
  };
  const dayNoteColor = (d) => (d.state === "missing" ? "error.dark" : "text.primary");

  /** 一天的狀態徽章。顏色以外一定有字（色盲分不出紅綠，硬規則也要求可點物件與狀態都帶文字） */
  const stateTag = (d) => {
    if (d.state === "rest") {
      return <StatusChip kind="closed">{d.holidayName ? `無須填寫 · ${d.holidayName}` : "無須填寫"}</StatusChip>;
    }
    if (d.state === "missing") {
      return (
        <StatusChip kind="attention" mark={<MdErrorOutline />}>
          未記錄
        </StatusChip>
      );
    }
    if (d.state === "future") {
      return <StatusChip kind="type">尚未到期</StatusChip>;
    }
    if (d.state === "filled") {
      return (
        <StatusChip kind="done" mark={<MdCheck />}>
          {/* 列得出來的才數得出筆數；全部都被權限擋住時只說「有記錄」，不要寫成 0 筆 */}
          {d.entries.length > 0 ? `${d.entries.length} 筆` : "已記錄"}
        </StatusChip>
      );
    }
    return <StatusChip kind="type">無法讀取</StatusChip>;
  };

  const noteSx = { px: 3, pt: 2, pb: 2.5 };

  return (
    <>
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
        <Typography variant="h1" component="h1">
          每日工項紀錄
        </Typography>
        <Typography variant="body2" color="text.secondary" component="span">
          {[whoLabel, rangeLabel].filter(Boolean).join(" · ")}
        </Typography>
      </Stack>
      <Typography sx={{ mt: "var(--space-2)" }}>
        工項即為每日日報。本頁檢視的是每日是否有填寫紀錄，而非工時。
      </Typography>

      {/* 看別人的紀錄要講清楚看的是誰：這一頁沒有第二個地方會顯示帳號 */}
      {isOther && (
        <Banner kind="info" role="status" sx={{ mt: "var(--space-4)" }}>
          目前檢視的是 <strong>{whoLabel}</strong> 的紀錄，而非您本人的紀錄。
        </Banner>
      )}

      {/* 週切換。圖示旁一定帶文字（硬規則 3），按鈕高度由主題的 --t-control-h 撐到 44px 以上 */}
      <Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        sx={{ flexWrap: "wrap", alignItems: "center", mt: "var(--space-4)" }}
      >
        <Button variant="outlined" startIcon={<MdChevronLeft aria-hidden="true" />} onClick={() => setWeekOffset((w) => w - 1)}>
          上一週
        </Button>
        <Typography component="span" sx={{ fontWeight: 600 }}>
          {rangeLabel}
        </Typography>
        <Button variant="outlined" endIcon={<MdChevronRight aria-hidden="true" />} onClick={() => setWeekOffset((w) => w + 1)}>
          下一週
        </Button>
        {weekOffset !== 0 && (
          <Button variant="text" onClick={() => setWeekOffset(0)}>
            返回本週
          </Button>
        )}
      </Stack>

      {/*
        一句話講完這一週的結論（硬規則 4：重要後果寫成 Banner，不要寫成灰字小字）。
        日期一定寫出來，不能只靠卡片上的紅色 —— 分不出顏色的人要照樣讀得到是哪幾天。
      */}
      <Box sx={{ mt: "var(--space-4)" }}>
        {loading && (
          /* role="status" 改由 InlineLoading 自己帶，提示條不再重複宣告 */
          <Banner kind="info" icon={false}>
            <InlineLoading text="本週紀錄載入中…" />
          </Banner>
        )}

        {!loading && unknown && (
          <Banner kind="warn" role="status" icon={<MdErrorOutline />}>
            <strong>無法讀取本週紀錄　</strong>
            系統未回傳本週的每日紀錄，下方各日狀態尚無法確認，請勿視為未填寫。
            若重新整理後仍無法讀取，請聯絡系統管理員。
          </Banner>
        )}

        {!loading && !unknown && missingDays.length > 0 && (
          <Banner
            kind="attention"
            role="alert"
            icon={<MdWarningAmber />}
            action={
              showReportBtn ? (
                /* 整頁唯一的主要按鈕（硬規則 1） */
                <Button variant="contained" onClick={() => navigate("/MyTask")}>
                  回報今日工作
                </Button>
              ) : null
            }
          >
            <strong>
              本週有 {missingDays.length} 天未記錄：
              {missingDays.map((d) => (d.date ? md(d.date) : "—")).join("、")}
            </strong>
            系統紀錄一律以送出當日為準，過去的日期無法事後補填。
          </Banner>
        )}

        {/*
          「都有記錄」只講已經到期的那幾天（dueWorkdayCount），還沒到的另外講。
          整週都還沒到（按「下一週 ›」）時沒有任何東西好講，只說還沒開始。
        */}
        {!loading && !unknown && missingDays.length === 0 && dueWorkdayCount > 0 && (
          <Banner kind="info" role="status" icon={<MdCheck />}>
            本週已過的 {dueWorkdayCount} 個工作日皆已記錄。
            {futureWorkdayCount > 0 && `尚有 ${futureWorkdayCount} 個工作日未到。`}
          </Banner>
        )}

        {!loading && !unknown && missingDays.length === 0 && dueWorkdayCount === 0 && futureWorkdayCount > 0 && (
          <Banner kind="info" role="status" icon={false}>
            本週尚未開始，{futureWorkdayCount} 個工作日皆未到。
          </Banner>
        )}
      </Box>

      {/* 一天一張卡。沒填的那一天照樣畫出來 —— 空的那一天正是這一頁要看的東西 */}
      <Stack spacing={2} sx={{ mt: "var(--space-4)" }}>
        {days.map((d) => (
          <Card key={d.key} component="section" sx={dayCardSx(d)}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "var(--space-2) var(--space-3)",
                px: 3,
                py: 2,
                ...(d.state === "rest" ? { bgcolor: "action.hover" } : {}),
              }}
            >
              <Typography variant="h3" component="span" sx={{ color: dayDateColor(d) }}>
                {d.label}
              </Typography>
              {stateTag(d)}
            </Box>

            {d.entries.map(entryRow)}

            {/*
              那一天有紀錄，但工項不在你看得到的專案裡。
              講出來才分得出「權限擋住」與「他真的沒填」—— 兩者在畫面上長得一模一樣。
            */}
            {d.hiddenCount > 0 && (
              <Typography sx={{ ...noteSx, color: dayNoteColor(d) }}>
                另有 {d.hiddenCount} 筆紀錄屬於您無權檢視的專案，無法列出內容。
              </Typography>
            )}

            {d.state === "missing" && (
              <Typography sx={{ ...noteSx, color: dayNoteColor(d) }}>
                {d.entries.length > 0
                  ? "當日僅有事後補登的紀錄，當天未留下紀錄。"
                  : "當日無任何工項紀錄。"}
              </Typography>
            )}
            {d.state === "future" && d.entries.length === 0 && (
              <Typography sx={{ ...noteSx, color: dayNoteColor(d) }}>尚未到該日期。</Typography>
            )}
            {d.state === "unknown" && d.entries.length === 0 && (
              <Typography sx={{ ...noteSx, color: dayNoteColor(d) }}>無法確認當日是否有紀錄（資料讀取失敗）。</Typography>
            )}
            {/* 「不用填」的日子沒有紀錄就整段不畫（硬規則 2）：那一天本來就不該有東西 */}
          </Card>
        ))}
      </Stack>
    </>
  );
}

/** 固定的空物件參考：避免每次 mapStateToProps 產生新 {} 讓 props 一直變 */
const EMPTY_WEEK = {};

const mapStateToProps = (state) => ({
  week: _.get(state, "Timesheet.Week", EMPTY_WEEK) || EMPTY_WEEK,
});

const mapDispatchToProps = (dispatch) => ({
  GET_TimesheetWeek: (payload, callback, loading) =>
    dispatch({ type: "GET_TimesheetWeek", payload, callback, loading }),
});

App.propTypes = {
  week: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  GET_TimesheetWeek: PropTypes.func,
};

App.defaultProps = {
  week: {},
  GET_TimesheetWeek: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
